"use client";

import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { useSearchParams } from "next/navigation";
import { usePathname } from "@/i18n/navigation";
import { getServiceByRoute } from "@/utils/constants/services/serviceCatalog";
import { useToast } from "@/hooks/useToast";
import { useUserLocation } from "@/hooks/location/useUserLocation";
import { useGeolocationPermission } from "@/hooks/location/useGeolocationPermission";
import { useNormalizeDateRange } from "@/hooks/useNormalizeDateRange";
import { useLocationSuggestions } from "@/hooks/search-form/useLocationSuggestions";
import { useSearchModuleQuery } from "@/hooks/search-form/useSearchModuleQuery";
import { useSearchSubmit } from "@/hooks/search-form/useSearchSubmit";
import type { LocationComboboxOption } from "@/components/LocationCombobox";
import type { DateRange } from "react-day-picker";
import {
  type SearchServiceKey,
  type SearchModuleFormValues,
  type QueryField,
  DEFAULT_SVC_TYPE,
  DEFAULT_VEHICLE_TYPE,
  DEFAULT_PICKUP_TIME,
  SERVICE_NAME_TO_SVC_TYPE,
  toSearchServiceKey,
  toNumberOrUndefined,
  toISODate,
  getServiceCapabilities,
  hasTupleChanged,
} from "@/components/search-form/search.modules.type";
import {
  getServiceHandler,
  type SearchFormSnapshot,
  type SearchQuerySnapshot,
} from "./searchFormHandlers";

export type InlineField =
  | "location"
  | "date"
  | "traveler"
  | "vehicleType"
  | "pickupType"
  | "explore"
  | "category"
  | null;

export type UseSearchFormParams = {
  selectedService?: SearchServiceKey | string;
  onSearchSuccess?: () => void;
  enableInlineExpand?: boolean;
  preserveCurrentPathOnSearch?: boolean;
  disableRentalType?: boolean;
  disableVehicleType?: boolean;
  disablePickupType?: boolean;
};

/**
 * Unified Search Form Controller Hook
 * ------------------------------------
 * Connects form state, destination suggestions, change detection & navigation.
 * Delegates service-specific comparison, validation, and reset rules to searchFormHandlers.
 */
export function useSearchForm({
  selectedService,
  onSearchSuccess,
  enableInlineExpand = false,
  preserveCurrentPathOnSearch = false,
}: UseSearchFormParams) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { toast } = useToast();

  // 1. UI state
  const [activeInlineField, setActiveInlineField] = useState<InlineField>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [locationErrorDismissed, setLocationErrorDismissed] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  // 2. Service & Handler resolution
  const activeService =
    selectedService ?? getServiceByRoute(pathname)?.key ?? "hotel";
  const serviceKey = toSearchServiceKey(activeService);
  const serviceHandler = useMemo(
    () => getServiceHandler(serviceKey),
    [serviceKey],
  );

  const isSearchPage = pathname.includes("/search");
  const shouldSyncUrlOnChange = !isSearchPage;

  const {
    hasLocation: supportsLocation,
    dateMode,
    extraField,
    isHotelSync,
  } = useMemo(() => getServiceCapabilities(serviceKey), [serviceKey]);
  const isTour = serviceKey === "tour_act";

  // 3. Top destination suggestions
  const urlServiceName = searchParams.get("svcType");
  const svcType =
    (urlServiceName && SERVICE_NAME_TO_SVC_TYPE[urlServiceName]) ||
    SERVICE_NAME_TO_SVC_TYPE[serviceKey] ||
    DEFAULT_SVC_TYPE;

  // 4. URL query state
  const query = useSearchModuleQuery(activeService);
  const {
    locationId: queryLocationId,
    search: querySearch,
    lat: queryLat,
    lng: queryLng,
    start: queryStart,
    end: queryEnd,
    date: queryDate,
    adults: queryAdults,
    children: queryChildren,
    rooms: queryRooms,
    vehicleType: queryVehicleType,
    pickupType: queryPickupType,
    rangeDateValue,
    singleDateValue,
    setRange,
    setDate,
    setTravelers,
    setVehicleType,
    setPickupType,
  } = query;

  // 5. Form state (react-hook-form)
  const { watch, setValue, control } = useForm<SearchModuleFormValues>({
    defaultValues: {
      locationId: queryLocationId,
      search: querySearch,
      lat: toNumberOrUndefined(queryLat),
      lng: toNumberOrUndefined(queryLng),
      rangeValue: rangeDateValue,
      singleDate: singleDateValue,
      adults: queryAdults,
      children: queryChildren,
      rooms: queryRooms,
      vehicleType:
        queryVehicleType ||
        (serviceKey === "rentals" ? DEFAULT_VEHICLE_TYPE : ""),
      pickupType:
        queryPickupType ||
        (serviceKey === "rentals" ? DEFAULT_PICKUP_TIME : ""),
    },
  });

  // Sync external query changes into form
  const prevQueryRef = useRef({
    locationId: queryLocationId,
    search: querySearch,
    lat: queryLat,
    lng: queryLng,
    start: queryStart,
    end: queryEnd,
    date: queryDate,
    adults: queryAdults,
    children: queryChildren,
    rooms: queryRooms,
    vehicleType: queryVehicleType,
    pickupType: queryPickupType,
  });

  const currentQueryValues: Record<QueryField, unknown> = useMemo(
    () => ({
      locationId: queryLocationId,
      search: querySearch,
      lat: queryLat,
      lng: queryLng,
      start: queryStart,
      end: queryEnd,
      date: queryDate,
      adults: queryAdults,
      children: queryChildren,
      rooms: queryRooms,
      vehicleType: queryVehicleType,
      pickupType: queryPickupType,
    }),
    [
      queryLocationId,
      querySearch,
      queryLat,
      queryLng,
      queryStart,
      queryEnd,
      queryDate,
      queryAdults,
      queryChildren,
      queryRooms,
      queryVehicleType,
      queryPickupType,
    ],
  );

  useEffect(() => {
    const prev = prevQueryRef.current;

    (Object.keys(currentQueryValues) as QueryField[]).forEach((key) => {
      if (currentQueryValues[key] === prev[key]) return;

      if (key === "search") {
        const hasLoc = Boolean(
          currentQueryValues.locationId ||
          (currentQueryValues.lat && currentQueryValues.lng),
        );
        if (hasLoc) {
          setValue("search", "");
          return;
        }
      }

      if (key === "lat" || key === "lng") {
        setValue(key, toNumberOrUndefined(currentQueryValues[key]) as never);
        return;
      }

      setValue(key as never, currentQueryValues[key] as never);
    });

    if (queryStart !== prev.start || queryEnd !== prev.end) {
      setValue(
        "rangeValue",
        queryStart || queryEnd
          ? {
              from: queryStart ? dayjs(queryStart).toDate() : undefined,
              to: queryEnd ? dayjs(queryEnd).toDate() : undefined,
            }
          : undefined,
      );
    }

    if (queryDate !== prev.date) {
      setValue(
        "singleDate",
        queryDate && dayjs(queryDate).isValid()
          ? dayjs(queryDate).toDate()
          : undefined,
      );
    }

    prevQueryRef.current = currentQueryValues as typeof prev;
  }, [
    currentQueryValues,
    queryLat,
    queryLng,
    queryStart,
    queryEnd,
    queryDate,
    setValue,
  ]);

  // Watched draft values
  const formLocationId = watch("locationId") ?? "";
  const formSearch = watch("search") ?? "";
  const formLat = watch("lat");
  const formLng = watch("lng");
  const formRangeValue = watch("rangeValue");
  const formSingleDate = watch("singleDate");
  const formAdults = watch("adults") ?? queryAdults;
  const formChildren = watch("children") ?? queryChildren;
  const formRooms = watch("rooms") ?? queryRooms;
  const formVehicleType =
    watch("vehicleType") ||
    queryVehicleType ||
    (extraField === "rental" ? DEFAULT_VEHICLE_TYPE : "");
  const formPickupType =
    watch("pickupType") ||
    queryPickupType ||
    (extraField === "rental" ? DEFAULT_PICKUP_TIME : "");

  const hasCoordinates =
    typeof formLat === "number" &&
    Number.isFinite(formLat) &&
    typeof formLng === "number" &&
    Number.isFinite(formLng);
  const hasLocation = Boolean(formLocationId || hasCoordinates);

  const normalizedStart = formRangeValue?.from
    ? toISODate(formRangeValue.from)
    : queryStart;
  const rawEnd = formRangeValue?.to ? toISODate(formRangeValue.to) : queryEnd;
  const normalizedEnd =
    isHotelSync && normalizedStart
      ? rawEnd && dayjs(rawEnd).isAfter(dayjs(normalizedStart), "day")
        ? rawEnd
        : dayjs(normalizedStart).add(1, "day").format("YYYY-MM-DD")
      : rawEnd;
  const normalizedFormSingleDate = toISODate(formSingleDate);

  // Snapshots for service handlers
  const formSnapshot: SearchFormSnapshot = useMemo(
    () => ({
      locationId: formLocationId,
      search: formSearch,
      lat: toNumberOrUndefined(formLat),
      lng: toNumberOrUndefined(formLng),
      rangeValue: formRangeValue,
      singleDate: formSingleDate,
      adults: formAdults,
      children: formChildren,
      rooms: formRooms,
      vehicleType: formVehicleType,
      pickupType: formPickupType,
      start: normalizedStart || "",
      end: normalizedEnd || "",
      singleDateString: normalizedFormSingleDate,
      hasLocation,
    }),
    [
      formLocationId,
      formSearch,
      formLat,
      formLng,
      formRangeValue,
      formSingleDate,
      formAdults,
      formChildren,
      formRooms,
      formVehicleType,
      formPickupType,
      normalizedStart,
      normalizedEnd,
      normalizedFormSingleDate,
      hasLocation,
    ],
  );

  const querySnapshot: SearchQuerySnapshot = useMemo(
    () => ({
      locationId: queryLocationId,
      search: querySearch,
      lat: toNumberOrUndefined(queryLat),
      lng: toNumberOrUndefined(queryLng),
      start: queryStart || "",
      end: queryEnd || "",
      date: queryDate || "",
      rangeValue: rangeDateValue,
      singleDateValue,
      adults: queryAdults,
      children: queryChildren,
      rooms: queryRooms,
      vehicleType: queryVehicleType,
      pickupType: queryPickupType,
    }),
    [
      queryLocationId,
      querySearch,
      queryLat,
      queryLng,
      queryStart,
      queryEnd,
      queryDate,
      rangeDateValue,
      singleDateValue,
      queryAdults,
      queryChildren,
      queryRooms,
      queryVehicleType,
      queryPickupType,
    ],
  );

  // Shared location suggestions (top destinations + hotel names)
  const { comboboxOptions, isComboboxLoading, sectionTitle } =
    useLocationSuggestions({
      svcType,
      search: formSearch,
      enableHotelSuggestions: isHotelSync,
      start: normalizedStart,
      end: normalizedEnd,
      adults: formAdults,
      children: formChildren,
      rooms: formRooms,
    });

  // Change detection via Tuples (Reload icon)
  const showReloadIcon = useMemo(() => {
    if (!isSearchPage) return false;
    const [formTuple, queryTuple] = serviceHandler.getComparisonTuples(
      formSnapshot,
      querySnapshot,
    );
    return hasTupleChanged(formTuple, queryTuple);
  }, [isSearchPage, serviceHandler, formSnapshot, querySnapshot]);

  // Location change check via Tuples (triggers newTab)
  const hasLocationDraftChange = useMemo(
    () => serviceHandler.isLocationChanged(formSnapshot, querySnapshot),
    [serviceHandler, formSnapshot, querySnapshot],
  );

  // Reset draft back to URL query via Tuples
  const resetDraftToQuery = useCallback(() => {
    const entries = serviceHandler.getResetEntries(querySnapshot);
    entries.forEach(([key, val]) => setValue(key, val as never));
  }, [serviceHandler, querySnapshot, setValue]);

  // Location GPS & combobox controller
  const { refetch: refetchUserLocation, isFetching: isFetchingLocation } =
    useUserLocation();
  const geolocationPermission = useGeolocationPermission();

  const handleLocationInputChange = useCallback(
    (value: string) => {
      if (!isHotelSync && !isTour) return;
      setValue("search", value);
      setValue("locationId", "");
      setValue("lat", undefined);
      setValue("lng", undefined);
    },
    [isHotelSync, isTour, setValue],
  );

  const handleLocationChange = useCallback(
    (value: string, option?: LocationComboboxOption) => {
      setLocationErrorDismissed(true);
      const isHotelOption = option?.kind === "hotel";

      if (isHotelSync && isHotelOption) {
        setValue("search", option?.label ?? value);
        setValue("locationId", "");
        setValue("lat", undefined);
        setValue("lng", undefined);
        return;
      }

      setValue("locationId", value);
      setValue("search", "");
      setValue("lat", undefined);
      setValue("lng", undefined);
    },
    [isHotelSync, setValue],
  );

  const handleUseCurrentLocation = useCallback(async () => {
    const result = await refetchUserLocation();
    const latVal = result?.data?.lat;
    const lngVal = result?.data?.lng;

    if (
      typeof latVal === "number" &&
      Number.isFinite(latVal) &&
      typeof lngVal === "number" &&
      Number.isFinite(lngVal)
    ) {
      setValue("locationId", "");
      setValue("search", "");
      setValue("lat", latVal);
      setValue("lng", lngVal);
    }
  }, [refetchUserLocation, setValue]);

  // Background hotel date normalization
  useNormalizeDateRange({
    checkin: queryStart,
    checkout: queryEnd,
    setRange,
    enabled: isHotelSync,
  });

  // Search submit hook
  const { submitSearch, isSubmitting } = useSearchSubmit({
    activeService,
    searchParams,
    onSearchSuccess,
    targetPathOverride: preserveCurrentPathOnSearch ? pathname : undefined,
    hotel: {
      adults: formAdults,
      children: formChildren,
      rooms: formRooms,
      start: normalizedStart,
      end: normalizedEnd,
      search: formSearch,
      hasCoordinates,
      lat: formLat,
      lng: formLng,
      locationId: formLocationId,
    },
    shared: {
      locationId: formLocationId,
      search: formSearch,
      lat: formLat,
      lng: formLng,
      start: normalizedStart,
      end: normalizedEnd,
      date: normalizedFormSingleDate,
      vehicleType: formVehicleType,
      pickupType: formPickupType,
    },
  });

  // Form validation via Tuples
  const validateForm = useCallback((): boolean => {
    const [isInvalid, errorField] =
      serviceHandler.getValidationTuple(formSnapshot);

    if (isInvalid) {
      setErrors({ [errorField]: true });
      return false;
    }

    setErrors({});
    return true;
  }, [serviceHandler, formSnapshot]);

  // Search submit handler
  const handleSearch = useCallback(async () => {
    if (isSubmitting) return;
    if (isDatePickerOpen) setIsDatePickerOpen(false);

    // Pre-submit service normalization (e.g. hotel checkout +1 day auto-fill)
    serviceHandler.onBeforeSubmit?.(formSnapshot, {
      setValue,
      setRange,
      shouldSyncUrl: shouldSyncUrlOnChange,
    });

    // Validation
    const isValid = validateForm();
    setLocationErrorDismissed(false);
    if (!isValid) return;

    // Submit mode logic via tuple condition:
    // [isNonSearchPage || hasLocationChanged] -> newTab, otherwise currentTab
    const submitMode =
      !isSearchPage || hasLocationDraftChange ? "newTab" : "currentTab";

    try {
      const didSubmit = await submitSearch(submitMode);
      if (!didSubmit) throw new Error("Search failed");

      // In search page, if opening new tab, revert form to match current page's results
      if (isSearchPage && submitMode === "newTab") {
        resetDraftToQuery();
      }
    } catch (error) {
      console.error("[SearchModule] search failed:", error);
      toast({
        variant: "error",
        title: "Search failed",
        description: "Unable to complete search. Please try again.",
      });
    }
  }, [
    isSubmitting,
    isDatePickerOpen,
    serviceHandler,
    formSnapshot,
    setValue,
    setRange,
    shouldSyncUrlOnChange,
    validateForm,
    isSearchPage,
    hasLocationDraftChange,
    submitSearch,
    resetDraftToQuery,
    toast,
  ]);

  // Input change handlers
  const handleSingleDateChange = useCallback(
    (val?: Date) => {
      setValue("singleDate", val);
      if (shouldSyncUrlOnChange) void setDate(val);
    },
    [setDate, setValue, shouldSyncUrlOnChange],
  );

  const handleRangeDateChange = useCallback(
    (val?: DateRange) => {
      setValue("rangeValue", val);
      if (
        shouldSyncUrlOnChange &&
        val?.from &&
        val?.to &&
        dayjs(val.to).isAfter(dayjs(val.from), "day")
      ) {
        void setRange(val.from, val.to);
      }
    },
    [setRange, setValue, shouldSyncUrlOnChange],
  );

  const handleVehicleTypeChange = useCallback(
    (val: string) => {
      setValue("vehicleType", val);
      if (shouldSyncUrlOnChange) setVehicleType(val);
    },
    [setValue, setVehicleType, shouldSyncUrlOnChange],
  );

  const handlePickupTypeChange = useCallback(
    (val: string) => {
      setValue("pickupType", val);
      if (shouldSyncUrlOnChange) setPickupType(val);
    },
    [setPickupType, setValue, shouldSyncUrlOnChange],
  );

  const handleTravelersChange = useCallback(
    (next: { adults: number; children: number; rooms: number }) => {
      setValue("adults", next.adults);
      setValue("children", next.children);
      setValue("rooms", next.rooms);
      if (shouldSyncUrlOnChange) {
        void setTravelers(next.adults, next.children, next.rooms);
      }
    },
    [setTravelers, setValue, shouldSyncUrlOnChange],
  );

  const hasExpandedInlineField =
    enableInlineExpand && activeInlineField !== null;

  return {
    // UI state
    activeInlineField,
    setActiveInlineField,
    hasExpandedInlineField,
    isDatePickerOpen,
    setIsDatePickerOpen,

    // Service capabilities
    supportsLocation,
    isHotelSync,
    isTour,
    dateMode,
    extraField,

    // Location field
    comboboxValue: formLocationId
      ? formLocationId
      : isHotelSync || isTour
        ? formSearch
        : "",
    hasCoordinates,
    isFetchingLocation,
    hasLocationError: Boolean(errors.location && !locationErrorDismissed),
    showCurrentLocation: geolocationPermission !== "denied",
    isComboboxLoading,
    comboboxOptions,
    sectionTitle,
    handleLocationChange,
    handleLocationInputChange,
    handleUseCurrentLocation,

    // Date field
    formSingleDate,
    formRangeValue,
    handleSingleDateChange,
    handleRangeDateChange,

    // Rental fields
    control,
    handleVehicleTypeChange,
    handlePickupTypeChange,

    // Traveler picker
    formAdults,
    formChildren,
    formRooms,
    handleTravelersChange,

    // Submit button
    isPending: isSubmitting,
    showReloadIcon,
    handleSearch,
  };
}
